import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Dropdown from '@components/Dropdown';
import Icon from '@components/Icon/Icon';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';

import DicomViewer from './DicomViewer';

const messages = defineMessages({
  openTests: {
    id: 'imagingTestsHistory.openTests',
    defaultMessage: 'OPEN TEST IMAGING',
  },
  chooseAStudy: {
    id: 'imagingTestsHistory.chooseAStudy',
    defaultMessage: 'Choose a Study',
  },
  close: {
    id: 'GLOBAL.close',
    defaultMessage: 'Close',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
});

interface DicomImagesProps {
  diagnostic_report?: DiagnosticReportRecord | null;
  is_sharing?: boolean;
}

const DicomImages: React.FC<DicomImagesProps> = ({
  diagnostic_report,
  is_sharing = false,
}) => {
  const { formatMessage } = useIntl();

  const [selectedStudyId, setSelectedStudyId] = useState<string>();
  const [sharingDialogOpen, setSharingDialogOpen] = useState<boolean>(false);

  const studies = diagnostic_report?.imagingStudies;
  const selectedStudy = studies?.find(({ id }) => id === selectedStudyId);
  const numStudies = studies?.length ?? 0;
  const handleDialogClose = () => setSharingDialogOpen(false);
  const handleDialogOpen = () => setSharingDialogOpen(true);

  const handleViewStudy = (studyId: string | undefined) => {
    if (!diagnostic_report) {
      return;
    }
    const study = studies?.find(({ id }) => id === studyId);
    // If authenticated user, navigate to dicom viewer page
    // Else open dialog (used for shared imaging test diagnostic report only atm)
    if (!is_sharing) {
      if (!study) {
        return;
      }
      navigate(
        `${ROUTES.IMAGING_TESTS}${diagnostic_report.id}${ROUTES.DICOM_VIEWER}?id=${study.id}`
      );
    } else {
      handleDialogOpen();
    }
  };

  const handleSelectStudy = (studyId: string) => {
    setSelectedStudyId(studyId);
  };

  return (
    <>
      {numStudies > 1 ? (
        <>
          <Dropdown
            label={formatMessage(messages.chooseAStudy)}
            onChange={handleSelectStudy}
            value={selectedStudyId}
          >
            {studies?.map((study, index) => (
              <option key={`study-${index}`} value={study.id ?? undefined}>
                {study.studyName}
              </option>
            ))}
          </Dropdown>

          <RoundedButton
            disabled={!selectedStudyId}
            iconName="images"
            onClick={() => handleViewStudy(selectedStudyId)}
            variant="white"
          >
            {formatMessage(messages.openTests)}
          </RoundedButton>
        </>
      ) : null}
      {numStudies === 1 ? (
        <RoundedButton
          iconName="images"
          onClick={() => {
            const study = studies?.at(0);
            if (study?.id) {
              handleViewStudy(study.id);
            }
          }}
          variant="white"
        >
          {formatMessage(messages.openTests)}
        </RoundedButton>
      ) : null}
      <Dialog
        fullScreen
        open={sharingDialogOpen}
        onClose={handleDialogClose}
        scroll="paper"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent={'space-between'}>
            {selectedStudy?.studyName}
            <Button
              color="primary"
              onClick={handleDialogClose}
              startIcon={<Icon name="xmark" size="lg" type="fas" />}
              variant="text"
            >
              {formatMessage(messages.close)}
            </Button>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DicomViewer
            reportId={diagnostic_report?.getId()}
            studyId={selectedStudyId}
            isSharing
            patientId={diagnostic_report?.patient ?? undefined}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DicomImages;
